import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { Box, Button, Grid, Modal, Typography, TextField, InputAdornment, TablePagination } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { showToast } from 'modules/layout/layout.actions';
import { fetchClientPreferredVendors, upsertClientPreferredVendor } from '../clients.actions';
import { isIdEqual } from 'modules/enumerations/enumerationHelpers';

const CLIENT_VENDOR_RELATIONSHIPS = [
  { id: '2F9A6B11-3A0E-4D33-8B6C-7C0F7A6A2D8E', label: 'Client Required' },
  { id: '8C64C4E3-7A57-4E6A-9B7D-0E2A8A5A9B31', label: 'Client Preferred' },
  { id: 'D0D3B8C6-8E71-4A9D-9B1A-4D2E6B7C1F90', label: 'Client Do Not Use' },
  { id: '00000000-0000-0000-0000-000000000000', label: 'None' }
];

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

const styles = theme => ({
  cardContainer: {
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.paper
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2)
  },
  modalContainer: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',

    backgroundColor: theme.palette.background.paper,
    opacity: 1,
    boxShadow: theme.shadows[24],
    borderRadius: 8,

    // Bigger sizing
    width: '92vw',
    maxWidth: 1400,
    height: '88vh',
    maxHeight: '88vh',

    // Layout/scrolling
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden' // container doesn't scroll; inner area does
  },

  // If you want the grid area to scroll while header/footer stay pinned:
  modalBody: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'auto'
  },
  gridHeaderCell: {
    padding: '8px'
  },
  vendorNameCell: {
    padding: '8px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  selectableCell: {
    height: 40,
    border: '1px solid #e0e0e0',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 4,
    '&:hover': {
      backgroundColor: '#f5f5f5'
    }
  },
  selectedDot: {
    width: 12,
    height: 12,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(2)
  }
});

const ClientVendorRelationshipGrid = props => {
  const {
    classes,
    clientId,
    vendors = [],
    clientVendorRelationships = [],
    showToast,
    fetchClientPreferredVendors,
    upsertClientPreferredVendor
  } = props;

  const [modalOpen, setModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);
  const [vendorRelationships, setVendorRelationships] = useState(clientVendorRelationships || []);

  const [clientVendorRelationshipForm, setClientVendorRelationshipForm] = useState({});

  const relationshipLookupFromApi = useMemo(() => {
    const map = {};
    (vendorRelationships || []).forEach(r => {
      if (r && r.vendorId != null) {
        map[r.vendorId] = { relationshipId: r.relationshipId.toUpperCase(), clientDiscountRate: r.clientDiscountRate };
      }
    });
    return map;
  }, [vendorRelationships]);

  const filteredVendors = useMemo(() => {
    const list = Array.isArray(vendors) ? vendors : [];
    if (!searchText.trim()) return list;
    const s = searchText.toLowerCase().trim();
    return list.filter(v => (v.companyName || '').toLowerCase().includes(s));
  }, [vendors, searchText]);

  const pagedVendors = useMemo(() => filteredVendors.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage), [
    filteredVendors,
    page,
    rowsPerPage
  ]);

  const openModal = async () => {
    setClientVendorRelationshipForm({ ...relationshipLookupFromApi });
    setSearchText('');
    setPage(0);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSearchText('');
  };

  const setVendorRelationship = (vendorId, relationshipId) => {
    setClientVendorRelationshipForm(prev => ({
      ...prev,
      [vendorId]: { relationshipId: relationshipId, clientDiscountRate: prev[vendorId]?.clientDiscountRate }
    }));
  };

  const setClientDiscount = (vendorId, clientDiscountRate) => {
    setClientVendorRelationshipForm(prev => ({
      ...prev,
      [vendorId]: {
        clientDiscountRate: clientDiscountRate,
        relationshipId:
          prev[vendorId]?.relationshipId || relationshipLookupFromApi[vendorId]?.relationshipId || CLIENT_VENDOR_RELATIONSHIPS[3].id
      }
    }));
  };

  const handleSave = async () => {
    const payload = filteredVendors.map(v => ({
      vendorId: v.vendorId,
      relationshipId: clientVendorRelationshipForm[v.vendorId]?.relationshipId || '00000000-0000-0000-0000-000000000000',
      clientDiscountRate:
        clientVendorRelationshipForm[v.vendorId]?.clientDiscountRate != null
          ? parseFloat(clientVendorRelationshipForm[v.vendorId].clientDiscountRate)
          : v.clientDiscountRate
    }));

    const request = { vendorRelationships: payload };

    setIsSaving(true);
    try {
      await upsertClientPreferredVendor(clientId, request);
      const resp = await fetchClientPreferredVendors(clientId);
      setVendorRelationships(resp.response || []);
      showToast('Vendor relationships updated successfully.', 'success');
      setModalOpen(false);
    } catch (e) {
      showToast('Error saving vendor relationships. Please try again.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const relationshipCounts = useMemo(() => {
    const counts = {};
    CLIENT_VENDOR_RELATIONSHIPS.forEach(r => (counts[r.id] = 0));
    (vendorRelationships || []).forEach(r => {
      if (r?.relationshipId && counts[r.relationshipId.toUpperCase()] != null) {
        counts[r.relationshipId.toUpperCase()] += 1;
      }
    });

    if (vendors && vendors.length > 0) {
      let vrCount = vendorRelationships ? vendorRelationships.length : 0;
      const noneCount = vendors.length - vrCount;
      counts['00000000-0000-0000-0000-000000000000'] = counts['00000000-0000-0000-0000-000000000000'] + noneCount;
    }

    return counts;
  }, [vendorRelationships, vendors]);

  return (
    <>
      <Box className={classes.cardContainer}>
        <Box className={classes.header}>
          <Box display="flex" alignItems="center" gap={12}>
            <FontAwesomeIcon icon={['fas', 'handshake']} size="2x" />
            <Typography variant="h5" style={{ marginLeft: '12px', fontSize: '1.3125rem', fontWeight: 'bold' }}>
              Vendor Relationships
            </Typography>
          </Box>

          <Button variant="contained" color="primary" onClick={openModal}>
            Edit
          </Button>
        </Box>
        <Box>
          {CLIENT_VENDOR_RELATIONSHIPS.map(r => (
            <Box key={r.id} display="flex" justifyContent="space-between">
              <Typography variant="body1">{r.label}</Typography>
              <Typography variant="body1" style={{ fontWeight: 600 }}>
                {relationshipCounts[r.id] || 0}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
      <Modal open={modalOpen} onClose={closeModal}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            p: 4,
            boxShadow: 24,
            width: 1000,
            maxHeight: '80vh',
            overflow: 'auto'
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Client Vendor Relationship Assignment</Typography>
            <Box>
              <Button onClick={closeModal} disabled={isSaving} style={{ marginRight: 8 }}>
                Cancel
              </Button>
              <Button variant="contained" color="primary" onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          </Box>
          <TextField
            placeholder="Search vendors..."
            variant="outlined"
            size="small"
            value={searchText}
            onChange={e => {
              setSearchText(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FontAwesomeIcon icon={['fas', 'search']} size="sm" />
                </InputAdornment>
              )
            }}
            sx={{ mb: 2, width: '100%' }}
          />
          <Grid container>
            <Grid item xs={2}>
              <Box sx={{ padding: '5px' }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Vendor
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={2}>
              <Box sx={{ padding: '5px' }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Client Discount Rate
                </Typography>
              </Box>
            </Grid>
            {CLIENT_VENDOR_RELATIONSHIPS.map(r => (
              <Grid item xs key={r.id}>
                <Box sx={{ padding: '5px' }}>
                  <Typography variant="subtitle1" fontWeight="bold" align="center">
                    {r.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
          {pagedVendors.map(vendor => {
            const selected = clientVendorRelationshipForm[vendor.vendorId] || {
              relationshipId: CLIENT_VENDOR_RELATIONSHIPS[3].id,
              clientDiscountRate: null
            };
            return (
              <Grid container key={vendor.vendorId} alignItems="center">
                <Grid item xs={2}>
                  <Box sx={{ padding: '5px' }}>{vendor.companyName}</Box>
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    variant="standard"
                    value={selected.clientDiscountRate ?? vendor.clientDiscountRate ?? null}
                    onChange={e => setClientDiscount(vendor.vendorId, e.target.value)}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      disableUnderline: false
                    }}
                    type="number"
                    inputProps={{
                      style: { fontSize: 12, padding: '6px 0' }
                    }}
                    onWheel={e => {
                      e.target.blur();
                    }}
                    sx={{
                      width: '100%',
                      mb: 0,
                      '& .MuiInput-underline:before': { borderBottomColor: '#e0e0e0' },
                      '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: '#bdbdbd' },
                      '& .MuiInput-underline:after': { borderBottomColor: '#3f51b5' }
                    }}
                  />
                </Grid>
                {CLIENT_VENDOR_RELATIONSHIPS.map(r => {
                  const isSelected = isIdEqual(selected.relationshipId, r.id);
                  return (
                    <Grid item xs key={r.id}>
                      <Box
                        sx={{
                          height: 40,
                          border: '1px solid #e0e0e0',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          borderRadius: 1,
                          '&:hover': {
                            backgroundColor: '#f5f5f5'
                          }
                        }}
                        onClick={() => setVendorRelationship(vendor.vendorId, r.id)}
                      >
                        {isSelected && <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'primary.main' }} />}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            );
          })}
          <TablePagination
            component="div"
            count={filteredVendors.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            onRowsPerPageChange={e => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            labelRowsPerPage="Vendors per page:"
          />
        </Box>
      </Modal>
    </>
  );
};

ClientVendorRelationshipGrid.propTypes = {
  classes: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired,
  clientId: PropTypes.number.isRequired,
  vendors: PropTypes.array,
  clientVendorRelationships: PropTypes.array,
  showToast: PropTypes.func.isRequired
};

export default compose(
  withStyles(styles, { withTheme: true }),
  connect(null, { showToast, fetchClientPreferredVendors, upsertClientPreferredVendor })
)(ClientVendorRelationshipGrid);
